
// Security validation utilities
export const validateServerName = (name: string): boolean => {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  return trimmed.length >= 3 && trimmed.length <= 100;
};

export const validateIPAddress = (ip: string): boolean => {
  if (!ip || typeof ip !== 'string') return false;
  
  const trimmed = ip.trim();
  if (trimmed.length === 0 || trimmed.length > 255) return false;
  
  // Allow IPv4 addresses (with optional port)
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}(:\d{1,5})?$/;
  if (ipv4Pattern.test(trimmed)) {
    // Validate each octet is 0-255
    const parts = trimmed.split(':')[0].split('.');
    return parts.every(part => {
      const num = parseInt(part, 10);
      return num >= 0 && num <= 255;
    });
  }
  
  // Allow domain names and hostnames (much more permissive)
  const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9\-\.]*[a-zA-Z0-9])?(:\d{1,5})?$/;
  
  return domainPattern.test(trimmed);
};

export const validateUsername = (username: string): boolean => {
  if (!username || typeof username !== 'string') return false;
  return /^[a-zA-Z0-9_-]{3,50}$/.test(username);
};

export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

export const validateComment = (content: string): boolean => {
  if (!content || typeof content !== 'string') return false;
  const trimmed = content.trim();
  
  // Check for malicious patterns
  const suspiciousPatterns = [
    /<script[^>]*>.*?<\/script>/gis,
    /<iframe[^>]*>.*?<\/iframe>/gis,
    /javascript:/gi,
    /vbscript:/gi,
    /data:text\/html/gi,
    /on\w+\s*=/gi
  ];
  
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(trimmed)) return false;
  }
  
  return trimmed.length >= 10 && trimmed.length <= 1000;
};

export const validateRating = (rating: number | null): boolean => {
  if (rating === null) return true;
  return typeof rating === 'number' && rating >= 1 && rating <= 5;
};

export const sanitizeInput = (input: string): string => {
  if (!input || typeof input !== 'string') return '';
  
  // Enhanced XSS protection
  let sanitized = input
    // Remove script tags and content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gis, '')
    // Remove iframe tags and content
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gis, '')
    // Remove object and embed tags
    .replace(/<(object|embed|applet)[^>]*>.*?<\/\1>/gis, '')
    // Remove javascript and vbscript protocols
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    // Remove data URLs that could contain HTML/JS
    .replace(/data:text\/html[^;]*;[^,]*,/gi, '')
    // Remove event handlers
    .replace(/on\w+\s*=/gi, '')
    // Remove style expressions (IE specific)
    .replace(/expression\s*\(/gi, '')
    // Remove link tags that could load external resources
    .replace(/<link[^>]*>/gi, '');
  
  // HTML encode remaining special characters
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
  
  return sanitized;
};

export const validateURL = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
};
